import { Settings } from '@streamr/tv-engine';
import { EventEmitter } from 'eventemitter3';
import { getPerfFlag } from './perfFlags';
/**
 * Analyses a local audio MediaStreamTrack via an AnalyserNode to detect
 * voice activity.  Emits debounced `speechStateChanged` and continuous
 * `audioLevelChanged` events.
 *
 * Runs on the main thread using the AudioContext already owned by
 * MediaManager — no extra decode overhead on the encoding hot-path.
 */
export class SpeechDetector extends EventEmitter {
    analyser;
    source;
    timeDomainData;
    intervalId;
    speaking = false;
    level = 0;
    aboveThresholdSince;
    belowThresholdSince;
    constructor(audioContext, track) {
        super();
        const stream = new MediaStream([track]);
        this.source = audioContext.createMediaStreamSource(stream);
        this.analyser = audioContext.createAnalyser();
        this.analyser.fftSize = 256;
        this.source.connect(this.analyser);
        this.timeDomainData = new Float32Array(this.analyser.fftSize);
        // Perf kill-switch (`?perf=speech:off`): the 50ms analyser poll is
        // an always-on main-thread cost; disabling it isolates that cost
        // (plus the resulting speech-state UI updates) in differential
        // measurements. With the poll off no speech events are emitted.
        if (getPerfFlag('speech', 'on') !== 'off') {
            this.intervalId = setInterval(() => this.poll(), Settings.speechDetectionPollIntervalMs);
        }
    }
    getAudioLevel() {
        return this.level;
    }
    getIsSpeaking() {
        return this.speaking;
    }
    destroy() {
        if (this.intervalId !== undefined) {
            clearInterval(this.intervalId);
            this.intervalId = undefined;
        }
        try {
            this.source.disconnect();
        }
        catch {
            // already disconnected
        }
        this.removeAllListeners();
    }
    poll() {
        this.analyser.getFloatTimeDomainData(this.timeDomainData);
        let sumOfSquares = 0;
        for (let i = 0; i < this.timeDomainData.length; i++) {
            const sample = this.timeDomainData[i];
            sumOfSquares += sample * sample;
        }
        const rms = Math.sqrt(sumOfSquares / this.timeDomainData.length);
        this.level = Math.min(1, rms);
        this.emit('audioLevelChanged', this.level);
        const now = performance.now();
        if (this.speaking) {
            if (rms < Settings.speechStopThreshold) {
                if (this.belowThresholdSince === undefined) {
                    this.belowThresholdSince = now;
                }
                else if (now - this.belowThresholdSince >=
                    Settings.speechStopDebounceMs) {
                    this.speaking = false;
                    this.belowThresholdSince = undefined;
                    this.aboveThresholdSince = undefined;
                    this.emit('speechStateChanged', false);
                }
            }
            else {
                this.belowThresholdSince = undefined;
            }
        }
        else {
            if (rms > Settings.speechStartThreshold) {
                if (this.aboveThresholdSince === undefined) {
                    this.aboveThresholdSince = now;
                }
                else if (now - this.aboveThresholdSince >=
                    Settings.speechStartDebounceMs) {
                    this.speaking = true;
                    this.aboveThresholdSince = undefined;
                    this.belowThresholdSince = undefined;
                    this.emit('speechStateChanged', true);
                }
            }
            else {
                this.aboveThresholdSince = undefined;
            }
        }
    }
}
